import { PlanInfo } from '@mm/state/helpers/billing-info-helpers';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';
import { Plan } from '@mm/state/interfaces/subscription';
import PromotionModel from '@mm/state/model/PromotionModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { isNumber } from 'lodash';
import React from 'react';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { useFreeTrialUpgradeModal } from '../../../../../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import OveragesManagement from '../../OveragesManagement/OveragesManagement';
import { planCanManageOverages } from '../BillingPageCard';
import PromoRedemptionSelector from '../PromoRedemptionSelector/PromoRedemptionSelector';
import UpgradeOrDowngradeButton from '../UpgradeOrDowngradeButton/UpgradeOrDowngradeButton';
import './BillingCardRightSection.pcss';

type Props = {
  messagesRemaining: number;
  page: EnhancedBillingPage;
  openRemoveFromTeamModal: () => void;
  downgradePlansIds: number[];
  upgradePlansIds: number[];
  subscriptionEndsAt: string;
  promotions: PromotionModel[];
  showPromotionsSelector: boolean;
  subscriptionIsCanceled?: boolean;
  outgoingMessagesLimit?: number;
  planInfo: Partial<PlanInfo>;
  plan?: Plan;
};

const getOverageText = (
  planInfo: Partial<PlanInfo>,
  isFreeTrialPromotion: boolean,
) => {
  if (isFreeTrialPromotion) {
    return 'Upgrade to continue sending messages';
  }
  if (planInfo.isFreePlan) {
    return 'Upgrade to get more sends for this page';
  }

  if (planInfo.isProPlan) {
    return 'To continue sending messages, upgrade your plan to Startup';
  }

  return 'To continue sending messages, enable overage payments';
};

// tslint:disable-next-line: cyclomatic-complexity
const BillingCardRightSection = (props: Props) => {
  const {
    downgradePlansIds,
    upgradePlansIds,
    openRemoveFromTeamModal,
    subscriptionEndsAt,
    page,
    subscriptionIsCanceled,
    messagesRemaining,
    promotions,
    showPromotionsSelector,
    outgoingMessagesLimit,
    planInfo,
    plan,
  } = props;

  const modalActions = useModalActions();

  const {
    isFreeTrialPromotionActive,
    isFreeTrialPromotionWithoutSubscription,
    isFreeTrialPromotionWithFutureSubscription,
  } = useFreeTrialUpgradeModal(page);

  const isFreeTrialWithHardLimitReach =
    isFreeTrialPromotionActive && page.hardLimitReached;

  const canManageOverages =
    planCanManageOverages(outgoingMessagesLimit, planInfo) ||
    (isFreeTrialWithHardLimitReach && !isFreeTrialPromotionWithoutSubscription);

  if (
    isFreeTrialPromotionWithFutureSubscription &&
    (page.softLimitReached || page.hardLimitReached)
  ) {
    return null;
  }

  if (!messagesRemaining && !page.overagesEnabled && !planInfo.isAnyTeamPlan) {
    const overageText = getOverageText(planInfo, isFreeTrialPromotionActive);
    const planExpiresText = `Plan Expires ${subscriptionEndsAt}`;

    return (
      <>
        <div className="billing-card-right-section__cancel-overage-wrapper">
          {subscriptionIsCanceled && (
            <div className="cancel-plan-button">{planExpiresText}</div>
          )}
        </div>

        <div className="billing-card-right-section billing-card-right-section--overage">
          <div className="billing-card-right-section__overage-title">
            Send Credits Limit reached
          </div>

          <div className="billing-card-right-section__overage-text">
            {overageText}
          </div>

          <div className="billing-card-right-section__overage-bottom">
            {canManageOverages && (
              <div>
                <OveragesManagement
                  facebookPage={page}
                  className="billing-card-right-section__enable-overages-button"
                  textOverride="Enable automatic upgrades"
                  hideQuestionIcon={true}
                />
              </div>
            )}

            {(planInfo.isFreePlan ||
              upgradePlansIds.length > 0 ||
              downgradePlansIds.length > 0) &&
              !subscriptionIsCanceled && (
                <UpgradeOrDowngradeButton
                  color={ButtonColor.transparent}
                  page={page}
                  upgradePlansIds={upgradePlansIds}
                  downgradePlansIds={downgradePlansIds}
                />
              )}

            {isFreeTrialPromotionWithoutSubscription && (
              <PrimaryButton
                className="billing-card-right-section__upgrade-button billing-card-right-section__overage-upgrade-button"
                color={ButtonColor.blue}
                onClick={() => modalActions.openFreeTrialUpgradeModal(page)}
              >
                Upgrade
              </PrimaryButton>
            )}

            <a
              className="billing-card-right-section__overage-management-link"
              href="https://mobilemonkey.com/help/article/03nc6a7axs-send-overage-automatic-upgrades"
              target="_blank"
              rel="noopener noreferrer"
            >
              View Pricing
            </a>

            {showPromotionsSelector && (
              <PromoRedemptionSelector
                facebookPageId={page.id}
                promotions={promotions}
              />
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="billing-card-right-section">
      {!planInfo.isAnyTeamPlan && (
        <div className="billing-card-right-section__upgrade-cancel-wrapper">
          {!subscriptionIsCanceled && (
            <UpgradeOrDowngradeButton
              page={page}
              upgradePlansIds={upgradePlansIds}
              downgradePlansIds={downgradePlansIds}
              disabled={isFreeTrialPromotionActive}
            />
          )}
          {isFreeTrialPromotionWithoutSubscription && (
            <PrimaryButton
              className="billing-card-right-section__upgrade-button"
              color={ButtonColor.blue}
              onClick={() => modalActions.openFreeTrialUpgradeModal(page)}
            >
              Upgrade
            </PrimaryButton>
          )}
          {showPromotionsSelector && (
            <PromoRedemptionSelector
              facebookPageId={page.id}
              promotions={promotions}
            />
          )}

          {subscriptionIsCanceled && (
            <div className="cancel-plan-button">
              Plan Expires {subscriptionEndsAt}
            </div>
          )}
        </div>
      )}
      {planInfo.isAnyTeamPlan && plan && isNumber(plan.pagesLimit) && (
        <div
          className="billing-card-right-section__remove-link"
          onClick={openRemoveFromTeamModal}
        >
          Remove
        </div>
      )}
    </div>
  );
};

export default BillingCardRightSection;
